/**
 * Fetches token strength criteria (DexScreener-aligned): token exists, has liquidity, mint revoked, freeze revoked.
 * Dispatches to Redux so token creator and liquidity pool creator stay in sync.
 */

import { getMintInfoCached } from '@/services/tokenMetadataService';
import { setStrengthCriteria, setStrengthLoading, setStrengthError } from '@/redux/slices/tokenStrengthSlice';
import { isLiquidityLocked } from '@/services/liquidityLockService';

const DEXSCREENER_API = 'https://api.dexscreener.com/token-pairs/v1/solana';

/**
 * Fetch strength criteria for a mint and dispatch to Redux.
 * @param {import('@solana/web3.js').Connection} connection
 * @param {string} mintAddress
 * @param {import('@reduxjs/toolkit').Dispatch} dispatch
 */
export async function fetchTokenStrengthCriteria(connection, mintAddress, dispatch) {
  if (!mintAddress || !dispatch) return;

  dispatch(setStrengthLoading(true));

  const result = {
    mintAddress,
    tokenExists: false,
    hasLiquidity: false,
    mintAuthorityRevoked: false,
    freezeAuthorityRevoked: false,
    liquidityLocked: false,
  };

  try {
    // 1. Mint info (token exists + mint/freeze authority)
    try {
      const mintInfo = await getMintInfoCached(connection, mintAddress);
      result.tokenExists = true;
      result.mintAuthorityRevoked = mintInfo.mintAuthority == null;
      result.freezeAuthorityRevoked = mintInfo.freezeAuthority == null;
    } catch {
      result.tokenExists = false;
    }

    // 2. DexScreener: has liquidity if any pairs exist
    try {
      const res = await fetch(`${DEXSCREENER_API}/${encodeURIComponent(mintAddress)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      const hasPairs = Array.isArray(data) && data.length > 0;
      result.hasLiquidity = hasPairs;

      // Best-effort LP mint detection from DexScreener response.
      // Schema may evolve, so we probe a few likely fields and fall back safely.
      if (hasPairs) {
        const primaryPair = data[0] || null;
        const lpMintAddress =
          primaryPair?.lpAddress ||
          primaryPair?.lpTokenAddress ||
          primaryPair?.liquidity?.lpTokenAddress ||
          null;

        if (lpMintAddress) {
          const lockInfo = await isLiquidityLocked(lpMintAddress, connection);
          result.liquidityLocked = !!lockInfo.locked;
        }
      }
    } catch {
      result.hasLiquidity = false;
      result.liquidityLocked = false;
    }

    dispatch(setStrengthCriteria(result));
  } catch (err) {
    dispatch(setStrengthError(err?.message || 'Failed to fetch token strength'));
    dispatch(setStrengthCriteria(result)); // still set what we have
  } finally {
    dispatch(setStrengthLoading(false));
  }
}
